import React, { useState, useRef, useLayoutEffect } from 'react';
import { getVariantClass, getIllustrationName, detectPlatform, formatLocationParts } from './calendarEventUtils';
import { ILLUSTRATIONS } from './CalendarEventIllustrations';

/**
 * Renders the location with a middle-dot separator when it fits on one
 * line, and drops the separator (stacks room name above address) when
 * the pill wraps to two lines.
 */
const LocationContent = ({ roomPart, addressPart }) => {
  const roomRef = useRef(null);
  const addressRef = useRef(null);
  const [sameLine, setSameLine] = useState(true);

  useLayoutEffect(() => {
    if (!addressPart || !roomRef.current || !addressRef.current) return undefined;
    const measure = () => {
      const roomTop = roomRef.current?.offsetTop ?? 0;
      const addrTop = addressRef.current?.offsetTop ?? 0;
      setSameLine(Math.abs(roomTop - addrTop) < 2);
    };
    measure();
    const parent = roomRef.current.parentElement;
    if (!parent || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [roomPart, addressPart]);

  return (
    <>
      <svg
        className="schedule-location-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
      <span className="schedule-location-text">
        <span className="schedule-location-room" ref={roomRef}>{roomPart}</span>
        {addressPart && (
          <>
            <span
              className="schedule-location-sep"
              aria-hidden="true"
              style={{ display: sameLine ? 'inline' : 'none' }}
            >
              ·
            </span>
            <span className="schedule-location-addr" ref={addressRef}>{addressPart}</span>
          </>
        )}
      </span>
    </>
  );
};

/**
 * Single calendar event card with themed colors and SVG illustration.
 *
 * All display values (time_range, duration, timezone_abbr) are pre-computed
 * by the backend — the frontend does NO timezone conversion.
 *
 * @param {Object} event - Event data from raw_output.events[]
 * @param {number} index - 0-based index (for variant cycling)
 * @param {function} [onBookRoom] - Called with the event when the user taps the
 *   "Book Meeting Room" button. Button is only rendered for future, timed
 *   events that carry an `id` AND when this prop is provided.
 */
const CalendarEventCard = ({ event, index, onBookRoom }) => {
  const variant = getVariantClass(index);
  const illustrationName = getIllustrationName(index);
  const IllustrationComponent = ILLUSTRATIONS[illustrationName];
  const platform = detectPlatform(event);

  const hasLocation = Boolean(event.location);

  // A Kiln room booking (not a Google meeting): it gets a "Room booked" mark,
  // and never a Book Meeting Room button — the room is already booked.
  const isRoomBooking = event.is_room_booking === true;

  // Book Meeting Room button visibility.
  // Hide when event.location is already set — the location pill already tells
  // the user where the meeting is, so offering to book a room is redundant.
  const eventStart = event.start_datetime ? new Date(event.start_datetime) : null;
  const isPast = eventStart instanceof Date && !isNaN(eventStart) && eventStart.getTime() < Date.now();
  const canBookRoom = Boolean(
    onBookRoom &&
    event.id &&
    !event.all_day &&
    !isPast &&
    !hasLocation &&
    !isRoomBooking
  );

  // Use pre-computed time_range_converted (already has gaps: "5:00pm - 6:00pm MDT")
  const timeDisplay = event.time_range_converted
    || `${event.start_time_converted || ''} - ${event.end_time_converted || ''} ${event.user_timezone_abbr || ''}`.trim();

  // Capitalize first letter of title
  const capitalizeFirst = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };
  // The badge already says "Room booked" — the title is the room and place.
  const rawTitle = isRoomBooking
    ? event.title?.replace(/^room booking:\s*/i, '')
    : event.title;
  const title = capitalizeFirst(rawTitle?.trim());

  return (
    <article className={`personal-schedule-card ${variant}${isRoomBooking ? ' is-room-booking' : ''}`}>
      <div className="schedule-card-content">
        {isRoomBooking && (
          <span className="schedule-room-booking-badge">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M13 4h3a2 2 0 0 1 2 2v14" />
              <path d="M2 20h3" />
              <path d="M13 20h9" />
              <path d="M10 12v.01" />
              <path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z" />
            </svg>
            Room booked
          </span>
        )}
        <div className="schedule-card-header">
          <h4 className="schedule-card-title">{title}</h4>
        </div>

        <div className="schedule-card-details">
          {/* Time row - uses pre-computed time_range with timezone */}
          {!event.all_day ? (
            <div className="schedule-detail-row">
              <svg className="schedule-icon" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="schedule-time">
                {timeDisplay}
                {event.duration ? ` \u00B7 ${event.duration}` : ''}
              </span>
            </div>
          ) : (
            <div className="schedule-detail-row">
              <svg className="schedule-icon" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="schedule-time">All Day</span>
            </div>
          )}

          {/* Attendees row + inline platform badge (Meet/Zoom/Teams).
              Showing the platform here keeps the footer focused on the
              location pill and avoids stacking conferencing info below it. */}
          {(event.attendee_count > 0 || (platform && event.conference_link)) && (
            <div className="schedule-detail-row schedule-detail-row--with-meta">
              {event.attendee_count > 0 && (
                <>
                  <svg className="schedule-icon" viewBox="0 0 24 24"
                       fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                  </svg>
                  <span className="schedule-attendees">
                    {event.attendee_count} {event.attendee_count === 1 ? 'Person' : 'People'}
                  </span>
                </>
              )}
              {platform && event.conference_link && (
                <a href={event.conference_link}
                   target="_blank"
                   rel="noopener noreferrer"
                   className={`schedule-platform-badge ${platform}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                  {platform === 'zoom' ? 'Zoom' : platform === 'meet' ? 'Meet' : 'Teams'}
                </a>
              )}
            </div>
          )}
        </div>

        {/* Footer: location pill only (platform now lives inline above).
            Clicking the pill opens Google Maps with the raw location as
            the search query — that's where users actually want to go from
            here (directions, street view, parking) rather than back to
            the calendar event page. */}
        {hasLocation && (() => {
          const parts = formatLocationParts(event.location);
          if (!parts) return null;
          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`;
          const content = <LocationContent roomPart={parts.roomPart} addressPart={parts.addressPart} />;
          return (
            <div className="schedule-card-footer">
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="schedule-location-badge schedule-location-badge--link"
                title="Open in Google Maps"
              >
                {content}
              </a>
            </div>
          );
        })()}

        {/* Book Meeting Room CTA — hidden for past / all-day / id-less events */}
        {canBookRoom && (
          <button
            type="button"
            className="schedule-card-book-btn"
            onClick={() => onBookRoom(event)}
            aria-label={`Book Meeting Room for ${title || 'this event'}`}
          >
            <svg
              className="schedule-card-book-btn-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="4" width="18" height="17" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <line x1="12" y1="14" x2="12" y2="18" />
              <line x1="10" y1="16" x2="14" y2="16" />
            </svg>
            <span>Book Meeting Room</span>
          </button>
        )}
      </div>

      {/* Themed illustration */}
      {IllustrationComponent && (
        <IllustrationComponent className="schedule-card-illustration" />
      )}
    </article>
  );
};

export default React.memo(CalendarEventCard);
